import { ORDER_TRACKING_BACKFILL_DAYS } from "@redotech/order-tracking-common/constants";
import { useTriggerLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { Text } from "@redotech/redo-web/text";
import { InputSchema, groupInput, input } from "@redotech/ui/form";
import { getDateTimeString } from "@redotech/util/date-format";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { RedoAdminClientContext } from "../../../client/context";
import { isSyncInProgress, syncTrackers } from "../../../client/orders";
import { useHasMarketingPermission } from "../../../marketing/use-marketing-permissions";
import { LoadTeamContext, TeamContext } from "../../team";

export const orderTrackingGeneralForm = groupInput({
  enabled: input<boolean>(),
  excludeTrackMyOrderClicks: input<boolean>(),
  smsEnabled: input<boolean>(),
  skipSmsOptInRequirement: input<boolean>(),
});

export type OrderTrackingGeneralForm = InputSchema.Input<
  typeof orderTrackingGeneralForm
>;

export type OrderTrackingGeneralValue = InputSchema.Value<
  typeof orderTrackingGeneralForm
>;

export const orderTrackingGeneralDefault: OrderTrackingGeneralValue = {
  enabled: false,
  excludeTrackMyOrderClicks: false,
  smsEnabled: false,
  skipSmsOptInRequirement: false,
};

export const OrderTrackingGeneralCard = memo(function OrderTrackingGeneralCard({
  input,
  perShipmentBillingEnabled,
}: {
  input: OrderTrackingGeneralForm;
  perShipmentBillingEnabled: boolean;
}) {
  const { enabled, excludeTrackMyOrderClicks } = input.inputs;

  const isAuthorized = useHasMarketingPermission("marketing_admin");

  return (
    <Card title="Order Tracking">
      <section className={gridCss.grid}>
        <div className={gridCss.span6L}>
          <RedoToggle
            description="Enable order and shipment tracking. First-time setup automatically backfills recent orders."
            disabled={
              !isAuthorized || (enabled.value && perShipmentBillingEnabled)
                ? ((enabled.value && perShipmentBillingEnabled
                    ? "Disable per-shipment billing before disabling Order Tracking"
                    : undefined) ?? true)
                : false
            }
            label="Order Tracking Enabled"
            setValue={enabled.setValue}
            size="sm"
            value={enabled.value}
          />
        </div>
        <div className={gridCss.span6L}>
          <RedoToggle
            description='Exclude "Track My Order" email clicks from upsell analytics'
            disabled={!isAuthorized}
            label="Exclude Track My Order Clicks"
            setValue={excludeTrackMyOrderClicks.setValue}
            size="sm"
            value={excludeTrackMyOrderClicks.value}
          />
        </div>
        <Flex className={gridCss.span6L}>
          <RedoToggle
            description="Enable SMS/MMS sending for order tracking"
            disabled={!isAuthorized}
            label="SMS"
            setValue={input.inputs.smsEnabled.setValue}
            size="sm"
            value={input.inputs.smsEnabled.value}
          />
        </Flex>
        <Flex className={gridCss.span6L}>
          <RedoToggle
            description="When enabled, transactional order tracking SMS messages will be sent to all customers unless they have explicitly unsubscribed. When disabled, customers must opt-in to receive transactional SMS."
            disabled={!isAuthorized}
            label="Skip SMS Opt-In Requirement"
            setValue={input.inputs.skipSmsOptInRequirement.setValue}
            size="sm"
            value={input.inputs.skipSmsOptInRequirement.value}
          />
        </Flex>
      </section>
    </Card>
  );
});

export const ShipmentTrackingCard = memo(function ShipmentTrackingCard({
  onAnalyzeTracking,
}: {
  onAnalyzeTracking: () => void;
}) {
  const team = useContext(TeamContext);
  const mostRecentSync = team?.settings.orderTracking?.mostRecentSync;

  const canCreateTrackers = team?.settings.orderTracking?.createTrackers;

  return (
    <Card title="Shipment tracking diagnostics">
      <Flex dir="column" gap="lg">
        <Flex gap="md">
          <RedoButton
            hierarchy="secondary"
            onClick={onAnalyzeTracking}
            size="sm"
            text="Analyze tracking capability"
          />
          {canCreateTrackers && <ManualSyncTrackersButton />}
        </Flex>
        {canCreateTrackers && (
          <Flex dir="column" gap="xs">
            <Text fontWeight="medium">Tracker sync status</Text>
            <div>
              {mostRecentSync ? (
                <>
                  <Text fontSize="sm">
                    Report time:{" "}
                    {getDateTimeString(new Date(mostRecentSync.syncDate))}
                  </Text>
                  <Text fontSize="sm">
                    Orders fully synced (last {ORDER_TRACKING_BACKFILL_DAYS}{" "}
                    days): {mostRecentSync.ordersFullySynced}
                  </Text>
                  <Text fontSize="sm">
                    Orders not fully synced (last {ORDER_TRACKING_BACKFILL_DAYS}{" "}
                    days): {mostRecentSync.ordersNotFullySynced}
                  </Text>
                </>
              ) : (
                <Text fontSize="sm" textColor="secondary">
                  Never synced
                </Text>
              )}
            </div>
          </Flex>
        )}
      </Flex>
    </Card>
  );
});

const POLL_FREQUENCY_MS = 5000;

const ManualSyncTrackersButton = memo(function ManualSyncTrackersButton() {
  const team = useContext(TeamContext);
  const client = useContext(RedoAdminClientContext);
  const loadTeam = useContext(LoadTeamContext);

  const [syncInProgress, setSyncInProgress] = useState(false);
  const pollingInProgress = useRef(false);

  const pollUntilFinished: () => void = () => {
    if (pollingInProgress.current) {
      return;
    }
    pollingInProgress.current = true;
    setTimeout(refreshInProgressStatus, POLL_FREQUENCY_MS);
  };

  useEffect(() => {
    if (!client || !team) {
      return;
    }
    isSyncInProgress(client, { teamId: team?._id })
      .then((inProgress) => {
        setSyncInProgress(inProgress);
        if (inProgress) {
          pollUntilFinished();
        }
      })
      .catch(() => console.error("Failed to check sync status"));
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  const [_, doStartSync] = useTriggerLoad(async (signal) => {
    if (!client || !team) {
      return;
    }
    setSyncInProgress(true);
    await syncTrackers(client, { teamId: team._id, signal });
    pollUntilFinished();
  });

  if (!team || !client) {
    return null;
  }

  const refreshInProgressStatus = async () => {
    const inProgress = await isSyncInProgress(client, { teamId: team._id });
    setSyncInProgress(inProgress);
    if (inProgress) {
      setTimeout(refreshInProgressStatus, POLL_FREQUENCY_MS);
    } else {
      pollingInProgress.current = false;
      loadTeam?.();
    }
  };

  return (
    <>
      <RedoButton
        disabled={syncInProgress}
        hierarchy="primary"
        onClick={() => doStartSync()}
        pending={syncInProgress}
        size="sm"
        text="Sync unsynced trackers"
      />
      {syncInProgress && (
        <Text fontSize="sm" textColor="warning">
          Sync in progress
        </Text>
      )}
    </>
  );
});
